import { AlertCircle } from "lucide-react";
import { CopyButton } from "~/components/assistant/CopyButton";
import { Skeleton } from "~/components/ui/skeleton";
import { ResourceLoadError } from "~/pages/manifold-nextgen/components/ResourceLoadError";
import {
  decodePublicDatasetRunKey,
  encodePublicDatasetRunKey,
  type SessionPublicDatasetRun,
} from "~/pages/manifold-nextgen/components/conversations/useSessionPublicDatasetRuns";
import { usePublicDatasetRunStatus } from "~/pages/manifold-nextgen/hooks/usePublicDatasetRunStatus";
import { AirflowRunState } from "~/services/publicDatasetRuns";
import { PublicDatasetTaskList } from "./PublicDatasetTaskList";
import { SessionContextDetailHeader } from "./SessionContextDetailHeader";
import type { SessionContextViewOption } from "./SessionContextDetailHeader";

interface PublicDatasetRunStatusDisplay {
  label: string;
  isLive: boolean;
  isCompleted: boolean;
  isFailed: boolean;
  isUnknown: boolean;
}

// `undefined` is a cold SWR cache key, not a running state — give it its own
// case so a not-yet-fetched old run doesn't render as live.
export function publicDatasetRunStatusDisplay(
  state: string | undefined
): PublicDatasetRunStatusDisplay {
  switch (state) {
    case undefined:
      return {
        label: "Loading…",
        isLive: false,
        isCompleted: false,
        isFailed: false,
        isUnknown: true,
      };
    case AirflowRunState.QUEUED:
      return {
        label: "Queued",
        isLive: true,
        isCompleted: false,
        isFailed: false,
        isUnknown: false,
      };
    case AirflowRunState.SUCCESS:
      return {
        label: "Succeeded",
        isLive: false,
        isCompleted: true,
        isFailed: false,
        isUnknown: false,
      };
    case AirflowRunState.FAILED:
      return {
        label: "Failed",
        isLive: false,
        isCompleted: false,
        isFailed: true,
        isUnknown: false,
      };
    case AirflowRunState.RUNNING:
    default:
      return {
        label: "Running",
        isLive: true,
        isCompleted: false,
        isFailed: false,
        isUnknown: false,
      };
  }
}

function formatTimestamp(value: string | null | undefined): string {
  if (!value) {
    return "—";
  }
  // Drop a doubly-terminated UTC offset ("+00:00Z") so Date.parse accepts it.
  // Matches SessionContextEvalRunDetail/SessionContextIngestDetail's guard.
  const normalized = value.replace(/([+-]\d{2}:?\d{2})Z$/, "$1");
  const ms = Date.parse(normalized);
  if (Number.isNaN(ms)) {
    return value;
  }
  const date = new Date(ms);
  const datePart = date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  const timePart = date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return `${datePart} at ${timePart}`;
}

interface SessionContextPublicDatasetRunDetailProps {
  dagId: string;
  runId: string;
  // Feeds the header switcher's view list.
  runs: SessionPublicDatasetRun[];
  onSelect: (dagId: string, runId: string) => void;
  onBack: () => void;
  onCollapse: () => void;
}

export const SessionContextPublicDatasetRunDetail = ({
  dagId,
  runId,
  runs,
  onSelect,
  onBack,
  onCollapse,
}: SessionContextPublicDatasetRunDetailProps) => {
  const { data: run, error, isLoading } = usePublicDatasetRunStatus(dagId, runId);
  const display = publicDatasetRunStatusDisplay(run?.state);
  const currentKey = encodePublicDatasetRunKey(dagId, runId);

  const views: SessionContextViewOption[] = runs.map((r) => ({
    value: encodePublicDatasetRunKey(r.dagId, r.runId),
    label: r.dagDisplayName ?? r.dagId,
  }));

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <SessionContextDetailHeader
        entityName="Pipeline runs"
        onEntityClick={onBack}
        onBack={onBack}
        onCollapse={onCollapse}
        views={views}
        currentView={currentKey}
        // A dag_id can run much longer than an eval-run tag or table name —
        // "Runs" avoids wrapping the header; the full name is still in the
        // dropdown items below and the "Pipeline" field in the body.
        currentLabel="Runs"
        onSetView={(value) => {
          const { dagId: nextDagId, runId: nextRunId } = decodePublicDatasetRunKey(value);
          onSelect(nextDagId, nextRunId);
        }}
      />

      {display.isLive && (
        <div className="h-0.5 w-full overflow-hidden" role="progressbar" aria-label="Running">
          <div className="h-full w-full origin-left bg-primary animate-indeterminate-bar" />
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-4">
        {isLoading && !run ? (
          <div className="flex flex-col gap-3" role="status" aria-label="Loading pipeline run">
            <Skeleton className="h-6 w-1/2 rounded-md" />
            <Skeleton className="h-24 rounded-lg" />
          </div>
        ) : error || !run ? (
          <ResourceLoadError error={error} resourceLabel="pipeline run" className="py-16" />
        ) : (
          <div className="flex flex-col gap-5">
            <dl className="flex flex-col gap-5">
              <Field label="Pipeline">
                <span className="break-words text-xs text-foreground">{run.dagId}</span>
              </Field>
              <Field label="Status">
                <span className="text-xs text-foreground">{display.label}</span>
              </Field>
              <Field label="Run ID">
                <span className="break-all text-xs text-foreground">{run.runId}</span>
                <CopyButton
                  text={run.runId}
                  variant="ghost"
                  size="sm"
                  className="ml-1.5 align-middle"
                />
              </Field>
              <Field label="Started">
                <span className="text-xs text-foreground">{formatTimestamp(run.startDate)}</span>
              </Field>
              <Field label="Ended">
                <span className="text-xs text-foreground">{formatTimestamp(run.endDate)}</span>
              </Field>
            </dl>

            {display.isFailed && (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3">
                <div className="mb-1.5 flex items-center gap-1.5">
                  <AlertCircle className="size-4 text-destructive" aria-hidden />
                  <span className="text-xs font-semibold text-destructive">Run failed</span>
                </div>
                <p className="break-words text-[11px] text-destructive">
                  The pipeline run failed. Check the task list below for which step failed.
                </p>
              </div>
            )}

            <div>
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Tasks
              </h3>
              <PublicDatasetTaskList tasks={run.tasks} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex min-w-0 flex-col gap-0.5">
    <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</dt>
    <dd className="min-w-0">{children}</dd>
  </div>
);
